<template>
  <article
    class="business-card"
    :class="`business-card--${themeKey || 'neutral'}`"
    :style="cardStyle"
    :aria-label="`Digitale Visitenkarte von ${contact.display_name || 'Kontakt'}`"
  >
    <div class="business-card__identity">
      <h3>{{ contact.display_name }}</h3>
    </div>

    <div class="business-card__photo" aria-hidden="true">
      <img v-if="photoUrl" :src="photoUrl" :alt="contact.display_name || ''">
      <span v-else>{{ initials }}</span>
    </div>

    <dl class="business-card__details">
      <div v-for="row in detailRows" :key="row.key">
        <dt>{{ row.label }}</dt>
        <dd v-if="row.href">
          <a :href="row.href" target="_blank" rel="noopener noreferrer">{{ row.value }}</a>
        </dd>
        <dd v-else>{{ row.value }}</dd>
      </div>
    </dl>

    <div class="business-card__qr" aria-label="QR-Code zum Speichern des Kontakts">
      <slot name="qr" />
    </div>
  </article>
</template>

<script setup>
import { computed } from 'vue';
import coreTwoCard from '../assets/business-cards/core-two.webp';
import solutionsCard from '../assets/business-cards/solutions.webp';
import partimusCard from '../assets/business-cards/partimus.webp';
import { getBusinessCardRows } from '../utils/businessCard.mjs';
import { getCardThemeKey } from '../utils/companyBranding.mjs';

const props = defineProps({
  contact: { type: Object, required: true },
  photoUrl: { type: String, default: null },
});

const backgrounds = {
  'core-two': coreTwoCard,
  solutions: solutionsCard,
  partimus: partimusCard,
};

const themeKey = computed(() => getCardThemeKey(props.contact.branding));
const cardStyle = computed(() => themeKey.value
  ? { backgroundImage: `url(${backgrounds[themeKey.value]})` }
  : {});
const detailRows = computed(() => getBusinessCardRows(props.contact));
const initials = computed(() => (props.contact.display_name || '')
  .split(/\s+/)
  .filter(Boolean)
  .map(part => part[0])
  .join('')
  .slice(0, 2)
  .toUpperCase() || '?');
</script>

<style scoped>
.business-card {
  position: relative;
  width: min(100%, 382px);
  aspect-ratio: 764 / 1120;
  overflow: hidden;
  color: white;
  background: linear-gradient(145deg, #253746, #526b7f);
  background-position: center;
  background-size: cover;
  border-radius: 1.5rem;
  box-shadow: 0 24px 55px rgb(15 23 42 / 28%);
  container-type: inline-size;
}

.business-card__identity {
  position: absolute;
  top: 17.5%;
  left: 7.5%;
  width: 62%;
  text-shadow: 0 1px 8px rgb(15 23 42 / 40%);
}

.business-card h3 {
  margin: 0;
  font-size: 6.8cqw;
  font-weight: 750;
  line-height: 1.05;
}

.business-card__photo {
  position: absolute;
  top: 16.5%;
  right: 7.5%;
  display: grid;
  width: 22%;
  aspect-ratio: 1;
  place-items: center;
  overflow: hidden;
  border: 2px solid rgb(255 255 255 / 55%);
  border-radius: 7%;
  color: white;
  background: rgb(37 55 70 / 72%);
  box-shadow: 0 10px 25px rgb(15 23 42 / 25%);
  font-size: 7cqw;
  font-weight: 700;
}

.business-card__photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.business-card__details {
  position: absolute;
  top: 29.5%;
  left: 7.5%;
  width: 85%;
  margin: 0;
  padding: 0;
}

.business-card__details div {
  display: grid;
  grid-template-columns: 27% 73%;
  align-items: baseline;
  min-height: 5.1cqw;
}
.business-card__details div + div { margin-top: 1cqw; }
.business-card__details dt { font-size: 2.45cqw; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; opacity: .74; }
.business-card__details dd { margin: 0; overflow-wrap: anywhere; font-size: 3.15cqw; line-height: 1.25; }
.business-card__details a { color: inherit; text-decoration: none; }
.business-card__details a:hover { text-decoration: underline; }

.business-card__qr {
  position: absolute;
  bottom: 6%;
  left: 50%;
  display: grid;
  width: 48%;
  aspect-ratio: 1;
  padding: 2.5%;
  place-items: center;
  overflow: hidden;
  transform: translateX(-50%);
  border-radius: 6%;
  background: white;
  box-shadow: 0 14px 34px rgb(15 23 42 / 28%);
}

.business-card__qr :deep(canvas) { display: block; width: 100% !important; height: 100% !important; }
.business-card--neutral .business-card__identity { top: 8%; }

@media (max-width: 420px) {
  .business-card { border-radius: 1.1rem; }
}
</style>
